/**
 * SS-3191: Reports › Campaigns, the new report. Pure helpers shared by the list and the detail
 * page (labels, sorting, which columns show).
 *
 * The api contract (GET /reports/outreach, GET /reports/outreach/:id, PUT /reports/outreach/:id/archive)
 * is documented in api controllers/outreachReport.js.
 */
import moment from "moment";

export const PURPOSE_LABELS = {
  "campaign-birthday": "Birthday",
  "campaign-manual": "Manual",
  "campaign-reactivation": "Reactivation",
  reminder: "Reminder",
  "campaign-review": "Review",
  "campaign-welcome": "Welcome",
  "campaign-recall": "Recare",
  "campaign-survey": "Survey",
  "campaign-confirmation": "Confirmation",
  "lead-ads": "Lead Ads",
};

export const purposeLabel = (purpose) =>
  PURPOSE_LABELS[purpose] ||
  String(purpose || "")
    .replace(/^campaign-/, "")
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

/** The goal choices offered in a campaign's settings, in plain words. */
export const GOAL_CHOICES = [
  { value: "appointment_booked", label: "Book an appointment" },
  { value: "appointment_confirmed", label: "Confirm an appointment" },
  { value: "payment_made", label: "Make a payment" },
  { value: "form_filled", label: "Fill a form" },
  { value: "email_opened", label: "Open the email", emailOnly: true },
  { value: "review_left", label: "Leave a review" },
  { value: "none", label: "No goal" },
];

export const goalChoicesFor = (channel) => GOAL_CHOICES.filter((choice) => !choice.emailOnly || channel === "email");

export const goalLabel = (goal) => {
  if (!goal || !goal.type) return "No goal";
  return GOAL_CHOICES.find((choice) => choice.value === goal.type)?.label || goal.label || goal.type;
};

export const FORMAT_LABELS = {
  email: "Email",
  sms: "SMS",
  phone: "Phone (IVR)",
  phone_ai: "Phone (AI)",
  drip: "Drip",
};

export const CHANNEL_LABELS = { email: "Email", sms: "SMS", phone: "Phone" };

/** "SMS · Phone · Drip · 5 steps" for a drip, the format label otherwise. */
export const formatLabel = (row) => {
  if (row?.drip) {
    const channels = (row.drip.channels || []).map((c) => CHANNEL_LABELS[c] || c);
    return [...channels, `Drip · ${row.drip.steps} steps`].join(" · ");
  }
  return FORMAT_LABELS[row?.format] || row?.format || "";
};

export const STATUS_LABELS = {
  active: "Active",
  goal_reached: "Goal reached",
  contacted: "Contacted",
  opted_out: "Opted out",
  exhausted: "Exhausted",
  paused: "Paused",
};

export const STEP_RESULT_LABELS = {
  answered: "Answered",
  no_answer: "No answer",
  voicemail: "Voicemail",
  busy: "Busy",
  sent: "Sent",
  delivered: "Delivered",
  failed: "Failed",
  booked: "Booked",
  skipped: "Skipped",
};

/** A tab hides the goal columns when no campaign in it has a goal. */
export const tabHasGoals = (rows) => (rows || []).some((row) => !!row.goal);

export const isReviewTab = (purpose) => purpose === "campaign-review";

export const createdByLabel = (row) => (row?.created_by?.type === "ai" ? "AI" : row?.created_by?.name || "—");

const sortValue = (row, key) => {
  switch (key) {
    case "updated_goal":
    case "created":
    case "sent_at":
    case "enrolled_at":
    case "goal_reached_at":
    case "appointment_at":
      return row?.[key] ? new Date(row[key]).getTime() : -Infinity;
    case "goal":
      return row?.goal ? goalLabel(row.goal) : "";
    case "format":
      return formatLabel(row);
    case "created_by":
      return createdByLabel(row);
    case "goal_percent":
    case "outreach":
    case "average_score":
    case "percent_answered":
    case "duration":
    case "steps_done":
    case "rating":
      return typeof row?.[key] === "number" ? row[key] : -Infinity;
    default: {
      const value = row?.[key];
      return value === null || value === undefined ? "" : String(value).toLowerCase();
    }
  }
};

const compare = (a, b) => {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
};

/**
 * Sorting rules of the report: a column's first click sorts it descending, the second ascending;
 * ties always fall back to `tieKey` (Updated Goal on the list, Sent At on the detail) descending.
 */
export const sortRows = (rows, { key, direction }, tieKey = "updated_goal") => {
  const sign = direction === "ascending" ? 1 : -1;
  return [...(rows || [])].sort((a, b) => {
    const primary = compare(sortValue(a, key), sortValue(b, key)) * sign;
    if (primary !== 0 || key === tieKey) return primary;
    return compare(sortValue(a, tieKey), sortValue(b, tieKey)) * -1;
  });
};

/**
 * First click on a column: descending (also on the column the table is sorted by by default);
 * a second click on the same column: ascending, and so on.
 */
export const nextSort = (current, key) => {
  if (current?.key === key && current.clicked) {
    return { key, direction: current.direction === "descending" ? "ascending" : "descending", clicked: true };
  }
  return { key, direction: "descending", clicked: true };
};

export const formatDate = (value) => (value ? moment(value).format("MMM D, YYYY") : "—");
export const formatDateTime = (value) => (value ? moment(value).format("MMM D, YYYY h:mm A") : "—");
export const formatPercent = (value) => (typeof value === "number" ? `${value}%` : "—");

export const formatDuration = (seconds) => {
  const s = Number(seconds);
  if (!Number.isFinite(s) || s <= 0) return "—";
  const mins = Math.floor(s / 60);
  const secs = s % 60;
  return mins ? `${mins}m ${secs}s` : `${secs}s`;
};

/** The search box's placeholder per format. */
export const searchPlaceholder = (format) => (format === "email" ? "Name or Email" : "Name or Number");

export const DATE_PRESETS = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 4 weeks", days: 28 },
  { label: "Last 3 months", days: 91 },
  { label: "Last 12 months", days: 365 },
];

export const presetRange = (days, now = new Date()) => [
  moment(now).subtract(days - 1, "days").startOf("day").toDate(),
  moment(now).endOf("day").toDate(),
];

/** The api's date params (MM/DD/YYYY, as getDateRange reads them). */
export const rangeParams = ([start, end]) => ({
  rangeBeg: start ? moment(start).format("MM/DD/YYYY") : "",
  rangeEnd: end ? moment(end).format("MM/DD/YYYY") : "",
});

/** A path at a location (bid), unless it names one already. */
export const withLocation = (path, bid) => (bid && !/[?&]bid=/.test(path) ? `${path}${path.includes("?") ? "&" : "?"}bid=${bid}` : path);

/** The new view is chosen per visit: only ?view=new opens it. */
export const isNewView = (search) => new URLSearchParams(search || "").get("view") === "new";
